import type { Dayjs } from 'dayjs';
import { last, sum, sumBy } from 'lodash-es';
import { date, formatUiDate } from '~/lib/date';
import { calculateDailyPriceByFeePeriod } from '~/models/common';

/** Period / segment row used for environmental-rate timelines in EA Current vs New. */
export type YearTabEnvSegment = {
  startDate?: string;
  endDate?: string;
  numOfDays?: number;
  contestableCost?: number;
  energyCost?: number;
  renewableCost?: number;
  regulatedCost?: number;
  meteringCharge?: number;
  retailerServiceFee?: number;
  networkCost?: number;
  totalCosts?: number;
  contestableAnnualCost?: number;
  peakConsumptionIncLoss?: number;
  offPeakConsumptionIncLoss?: number;
  shoulderConsumptionIncLoss?: number;
  totalConsumptionIncLoss?: number;
  peakConsumptionExcludeLoss?: number;
  offPeakConsumptionExcludeLoss?: number;
  shoulderConsumptionExcludeLoss?: number;
  totalConsumptionExcludeLoss?: number;
  meterContestableCosts?: Record<string, number>;
  meterEnergyCosts?: Record<string, number>;
  meterRenewableCosts?: Record<string, number>;
  meterRetailerServiceFee?: Record<string, number>;
  meterMeteringCharge?: Record<string, number>;
};

function scaleMeterCostRecord(
  rec: Record<string, number> | undefined,
  ratio: number
): Record<string, number> | undefined {
  if (!rec || Object.keys(rec).length === 0) return undefined;
  return Object.fromEntries(Object.entries(rec).map(([meterId, v]) => [meterId, v * ratio]));
}

type PeriodWithTimeline = {
  startDate?: string;
  endDate?: string | null;
  periodName?: number;
  environmentalRatePeriods?: { endDate?: string; startDate?: string }[];
};

/**
 * Contract end for clipping the last tab (period endDate, else last segment end).
 * Interpreted as the **last inclusive calendar day** on contract (same as segment `endDate` strings from the API).
 */
export function expectedContractEndDate(period: PeriodWithTimeline): Dayjs {
  const start = period.startDate ?? '';
  if (period.endDate != null && period.endDate !== '') {
    return date(period.endDate);
  }
  const segs = period.environmentalRatePeriods;
  if (segs?.length) {
    return date(last(segs)?.endDate ?? start);
  }
  return date(start);
}

/** When `periodName` is missing, derive how many 12-month tabs fit the contract using calendar start → end (same window as clipping). */
export function numberOfYearTabsFromContractDates(period: PeriodWithTimeline): number {
  const segs = period.environmentalRatePeriods;
  if (!segs?.length) return 0;
  const startRaw = period.startDate ?? segs[0]?.startDate;
  if (startRaw == null || startRaw === '') return 0;
  const startD = date(startRaw);
  const endD = expectedContractEndDate(period);
  if (!startD.isValid() || !endD.isValid()) return 0;
  const monthSpan = Math.max(0, Math.ceil(endD.diff(startD, 'month', true)));
  return monthSpan > 0 ? Math.ceil(monthSpan / 12) : 0;
}

/** Tab count for Current vs New: prefer `periodName` (months), else calendar months from the same period used in the detail view. */
export function numberOfYearTabsForOfferPeriod(period: PeriodWithTimeline): number {
  if (!period.environmentalRatePeriods?.length) return 0;
  if (period.periodName != null && period.periodName > 0) {
    return Math.ceil(period.periodName / 12);
  }
  return numberOfYearTabsFromContractDates(period);
}

export function yearTabStart(contractStart: string, yearIndex: number): Dayjs {
  return date(contractStart).add(yearIndex, 'year');
}

/** Nominal exclusive right edge of the Nth year slice `[yearTabStart, nominalYearTabEnd)` (not clipped to contract end). */
export function nominalYearTabEnd(contractStart: string, yearIndex: number): Dayjs {
  return date(contractStart).add(yearIndex + 1, 'year');
}

/** First midnight strictly after the last **inclusive** calendar day on contract (for `[start, exclusiveEnd)` math). */
export function exclusiveEndAfterInclusiveLastDay(inclusiveLastDay: Dayjs): Dayjs {
  return inclusiveLastDay.add(1, 'day');
}

/**
 * Exclusive right edge of `[yearTabStart, …)` for tab `yearIndex`, clipped so the last **inclusive** contract day
 * stays inside the window. `contractEndInclusive` is the last on-contract calendar day; its exclusive bound is
 * start-of-day the **next** calendar day (otherwise `date('YYYY-12-31')` at midnight excludes all of Dec 31).
 */
export function clippedYearTabEnd(contractStart: string, yearIndex: number, contractEndInclusive: Dayjs): Dayjs {
  const nominalExclusiveEnd = nominalYearTabEnd(contractStart, yearIndex);
  const contractExclusiveEnd = exclusiveEndAfterInclusiveLastDay(contractEndInclusive);
  return nominalExclusiveEnd.valueOf() < contractExclusiveEnd.valueOf() ? nominalExclusiveEnd : contractExclusiveEnd;
}

export function formatYearTabDateRange(contractStart: string, yearIndex: number, contractEnd: Dayjs) {
  const start = yearTabStart(contractStart, yearIndex);
  const endExclusive = clippedYearTabEnd(contractStart, yearIndex, contractEnd);
  return {
    tabStartFormatted: formatUiDate(start.format('YYYY-MM-DD')),
    tabEndFormatted: formatUiDate(endExclusive.subtract(1, 'day').format('YYYY-MM-DD'))
  };
}

/**
 * Intersect each segment with [yearStart, yearEnd) and prorate numeric fields by overlap / segment days.
 * Same rules as the new-contract bill (inclusive segment end except when clipped to the window end).
 * Prorates segment totals, per-meter cost maps, network/totalCosts, consumption (inc/ex loss), and annual contestable
 * so clipped rows stay internally consistent (e.g. meterEnergyCosts matches energyCost for the same ratio).
 */
export function clipEnvironmentalSegmentsToYearTab<T extends YearTabEnvSegment>(
  segments: T[],
  yearStart: Dayjs,
  yearEnd: Dayjs
): T[] {
  const yearStartTs = yearStart.valueOf();
  const yearEndTs = yearEnd.valueOf();
  const overlapping = segments.filter((p) => {
    const periodStart = date(p.startDate).valueOf();
    const periodEnd = date(p.endDate).valueOf();
    return periodStart < yearEndTs && periodEnd > yearStartTs;
  });
  return overlapping.map((p) => {
    const periodStart = date(p.startDate);
    const periodEnd = date(p.endDate);
    const clippedStart = periodStart.valueOf() > yearStartTs ? periodStart : yearStart;
    const clippedEnd = periodEnd.valueOf() < yearEndTs ? periodEnd : yearEnd;
    const diffDays = Math.max(0, date(clippedEnd).diff(date(clippedStart), 'day'));
    const isWindowEndClip = clippedEnd.valueOf() === yearEndTs;
    const clippedDays = isWindowEndClip ? diffDays : diffDays + 1;
    const segDays = p.numOfDays ?? 0;
    const ratio = segDays > 0 ? clippedDays / segDays : 0;
    const meterContestableCosts = scaleMeterCostRecord(p.meterContestableCosts, ratio);
    const meterEnergyCosts = scaleMeterCostRecord(p.meterEnergyCosts, ratio);
    const meterRenewableCosts = scaleMeterCostRecord(p.meterRenewableCosts, ratio);
    const meterRetailerServiceFee = scaleMeterCostRecord(p.meterRetailerServiceFee, ratio);
    const meterMeteringCharge = scaleMeterCostRecord(p.meterMeteringCharge, ratio);
    return {
      ...p,
      numOfDays: clippedDays,
      contestableCost: (p.contestableCost ?? 0) * ratio,
      energyCost: (p.energyCost ?? 0) * ratio,
      renewableCost: (p.renewableCost ?? 0) * ratio,
      regulatedCost: (p.regulatedCost ?? 0) * ratio,
      meteringCharge: (p.meteringCharge ?? 0) * ratio,
      retailerServiceFee: (p.retailerServiceFee ?? 0) * ratio,
      networkCost: (p.networkCost ?? 0) * ratio,
      totalCosts: (p.totalCosts ?? 0) * ratio,
      contestableAnnualCost: (p.contestableAnnualCost ?? 0) * ratio,
      ...(p.peakConsumptionIncLoss != null && { peakConsumptionIncLoss: p.peakConsumptionIncLoss * ratio }),
      ...(p.offPeakConsumptionIncLoss != null && { offPeakConsumptionIncLoss: p.offPeakConsumptionIncLoss * ratio }),
      ...(p.shoulderConsumptionIncLoss != null && { shoulderConsumptionIncLoss: p.shoulderConsumptionIncLoss * ratio }),
      ...(p.totalConsumptionIncLoss != null && { totalConsumptionIncLoss: p.totalConsumptionIncLoss * ratio }),
      ...(p.peakConsumptionExcludeLoss != null && { peakConsumptionExcludeLoss: p.peakConsumptionExcludeLoss * ratio }),
      ...(p.offPeakConsumptionExcludeLoss != null && {
        offPeakConsumptionExcludeLoss: p.offPeakConsumptionExcludeLoss * ratio
      }),
      ...(p.shoulderConsumptionExcludeLoss != null && {
        shoulderConsumptionExcludeLoss: p.shoulderConsumptionExcludeLoss * ratio
      }),
      ...(p.totalConsumptionExcludeLoss != null && {
        totalConsumptionExcludeLoss: p.totalConsumptionExcludeLoss * ratio
      }),
      ...(meterContestableCosts && { meterContestableCosts }),
      ...(meterEnergyCosts && { meterEnergyCosts }),
      ...(meterRenewableCosts && { meterRenewableCosts }),
      ...(meterRetailerServiceFee && { meterRetailerServiceFee }),
      ...(meterMeteringCharge && { meterMeteringCharge })
    } as T;
  });
}

type MeterWithNetwork = {
  annualData?: {
    networkStanding?: number;
    networkChargesEnergy?: number;
    networkChargesDemand?: number;
  };
};

export function networkChargesBreakdownForDays(meters: MeterWithNetwork[], totalNumOfDays: number, divide: number) {
  const standing =
    calculateDailyPriceByFeePeriod(sumBy(meters, 'annualData.networkStanding')) * totalNumOfDays * divide;
  const energy =
    calculateDailyPriceByFeePeriod(sumBy(meters, 'annualData.networkChargesEnergy')) * totalNumOfDays * divide;
  const demand =
    calculateDailyPriceByFeePeriod(sumBy(meters, 'annualData.networkChargesDemand')) * totalNumOfDays * divide;
  return { standing, energy, demand };
}

/** Standing + energy + demand network charges for a calendar slice of `totalNumOfDays` days. */
export function sumNetworkChargesForDays(meters: MeterWithNetwork[], totalNumOfDays: number, divide: number): number {
  const { standing, energy, demand } = networkChargesBreakdownForDays(meters, totalNumOfDays, divide);
  return sum([standing, energy, demand]);
}
